import {
    CRM_DOCUMENT_FIELD_LABELS as L,
    CRM_DOCUMENT_FILE_FIELDS,
    CrmDocumentCardTemplate,
    crmDocumentFileFields,
} from '@/Containers/CrmSection/Components/Documents'
import { renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { CARRIER_DOCUMENT_REST_URL } from '../Contracts/ICarrier'

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

/** Лише перегляд: ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/** Word і PDF — одним кроком у збережений документ (`SPEC-008` §5.7.1). */
const FILE_FIELDS = crmDocumentFileFields(CARRIER_DOCUMENT_REST_URL)

/**
 * Поля, яких бек від форми не приймає:
 *  - службові й назву ставить він сам (назва — з номера й дати, КЛ-28);
 *  - файли вантажаться окремими запитами, а не збереженням форми;
 *  - `carrier` задається лише при створенні — змінити власника не можна
 *    (`SPEC-008` §5.1).
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
    ...CRM_DOCUMENT_FILE_FIELDS,
] as const

const prepareDocumentPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    if (typeof formContext?.values?.['@id'] === 'string') delete values['carrier']
}

/**
 * Документи перевізника — «Друковані форми» (`SPEC-010` §5.4, §7.3, §9.6,
 * `SPEC-008` §4.4) — ті самі поля й картка, що в документа клієнта.
 *
 * Власного реєстру немає — лише вкладка в картці верифікованого перевізника
 * (R-13), тож без `pageUrl`. Створюють документ тільки звідти
 * (`CREATED_FROM_PARENT`): вручну — цією формою, генерацією — меню S7a.
 *
 * Права — лише з беку (`SPEC-003` §1.3): документ веде той, хто змінює
 * перевізника, — `_permissions` документа дорівнюють `update` перевізника з
 * правилом «лише логіст» (R-06). Ролей ресурс не питає.
 *
 * «Тип документа» — лише типи для картки перевізника з провайдером
 * «Перевізник» (`SPEC-010` §5.4, `SPEC-012` N-19).
 *
 * Word і PDF вантажаться одним кроком у збережений документ — ресурс у
 * `ONE_STEP_UPLOADS` вартового завантажень.
 */
export const CarrierDocumentResource: IResourceConfig = {
    title: 'Документи перевізника',
    fields: {
        documentType: {
            label: L.documentType,
            type: 'select',
            resource: 'Setting.ListDocumentType',
            isRequired: true,
            inputProps: { filter: { isUseInCarrier: true, provider: 'carrier' } },
        },
        title: readOnly({
            label: L.title,
        }),
        number: {
            label: L.number,
            inputProps: { maxLength: 150 },
        },
        date: {
            label: L.date,
            type: 'date',
            view: renderDateView,
        },
        wordFile: {
            ...FILE_FIELDS.wordFile,
            type: 'file',
        },
        pdfFile: {
            ...FILE_FIELDS.pdfFile,
            type: 'file',
        },
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                CarrierDocumentPayload: prepareDocumentPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: CrmDocumentCardTemplate },
            edit: { template: CrmDocumentCardTemplate },
            create: { template: CrmDocumentCardTemplate },
        },
    },
    // Як у легасі (`CarrierDocumentCrud.jsx:69`)
    drawer: {
        width: 600,
    },
    restUrl: CARRIER_DOCUMENT_REST_URL,
}
